/// <reference types="@types/google.maps" />

import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { PIN_ART_TIP } from '@fhs/maps/src/components/markers/store-pin-paths';
import { Box } from '@fhs-legacy/universal-components';
import useGoogleGeolocationLibrary from 'hooks/geolocation/use-google-geolocation-library';
import { useLocale } from 'state/intl';
import { getRandomId } from 'utils';
import { isValidPosition } from 'utils/geolocation';
import DEFAULT_MAP_ZOOM from 'utils/restaurant/default-map-zoom';

import mapDefaults from './defaults';
import { svgToUri } from './icons';
import { deliveryAddressMarkerSvg } from './icons/delivery-address-marker';
import { destinationMarkerSvg } from './icons/destination-marker';
import { driverMarkerSvg } from './icons/driver-marker';
import { storeMarkerSvg } from './icons/store-marker';
import { storeActiveMarkerSvg } from './icons/store-marker-active';
import { storeDisabledMarkerSvg } from './icons/store-marker-disabled';
import { storeFavoriteMarkerSvg } from './icons/store-marker-fav';
import { storeFavoriteClosedMarkerSvg } from './icons/store-marker-fav-closed';
import {
  storeSelectedClosedMarkerSvg,
  storeSelectedOpenMarkerSvg,
} from './icons/store-marker-selected';
import { userMarkerSvg } from './icons/user-marker';
import { isPinMarkerType, isSelectedStoreMarkerType } from './marker-utils';
import {
  CenterPoint,
  FitAndCenterFromCoordsFn,
  IMarkerInput,
  IUseMapHook,
  MarkerTypes,
  PanToFn,
} from './types';

const MapContainer = Box.withConfig({
  backgroundColor: Styles.color.white,
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 'auto',
  height: 'full',
  width: 'full',
});

const markersUris = {
  [MarkerTypes.User]: svgToUri(userMarkerSvg),
  [MarkerTypes.StoreActive]: svgToUri(storeMarkerSvg),
  [MarkerTypes.StoreInActive]: svgToUri(storeActiveMarkerSvg),
  [MarkerTypes.StoreDisabled]: svgToUri(storeDisabledMarkerSvg),
  [MarkerTypes.StoreFavOpen]: svgToUri(storeFavoriteMarkerSvg),
  [MarkerTypes.StoreFavClosed]: svgToUri(storeFavoriteClosedMarkerSvg),
  [MarkerTypes.Destination]: svgToUri(destinationMarkerSvg),
  [MarkerTypes.Driver]: svgToUri(driverMarkerSvg),
  [MarkerTypes.StoreSelectedOpen]: svgToUri(storeSelectedOpenMarkerSvg),
  [MarkerTypes.StoreSelectedClosed]: svgToUri(storeSelectedClosedMarkerSvg),
  [MarkerTypes.DeliveryAddress]: svgToUri(deliveryAddressMarkerSvg),
};

const MIN_MAP_ZOOM = 5;
const MAX_MAP_ZOOM = 20;

const useMap: IUseMapHook = ({
  eventListeners = {},
  position,
  initialZoom,
  disableControls = false,
} = {}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const markers = useRef<Record<string, { marker: google.maps.Marker; type: MarkerTypes }>>({});
  // track when the map container has been loaded into the dom
  const [isMapMounted, setIsMapMounted] = useState(false);
  const domRef = useRef<HTMLDivElement | null>(null);
  const { libraryLoaded } = useGoogleGeolocationLibrary();

  const { region } = useLocale();
  const defaultPosition = mapDefaults[region] ?? mapDefaults.default;
  const startPosition = position
    ? {
        lat: position.lat,
        lng: position.lng,
      }
    : defaultPosition;
  const [center, setCenter] = useState<CenterPoint>(startPosition);
  const [zoom, setZoom] = useState(
    initialZoom ?? (startPosition !== defaultPosition ? defaultPosition.zoom : DEFAULT_MAP_ZOOM)
  );

  const mapListeners = useRef<Record<string, google.maps.MapsEventListener[]>>({});
  const addListener = (eventName: string, map: google.maps.Map, cb: Function, id: string) => {
    if (!mapListeners.current[id]) {
      mapListeners.current[id] = [];
    }

    const dereg = map.addListener(eventName, cb);
    mapListeners.current[id] = mapListeners.current[id].concat(dereg);
  };

  const clearListenerCache = (id: string) => {
    const handlers = mapListeners.current[id] || [];
    handlers.forEach(handler => {
      if (typeof handler.remove === 'function') {
        handler.remove();
      }
    });
    mapListeners.current[id] = [];
  };

  /*
   * SETUP MAP
   *
   * Instantiates the map on the already-loaded Maps API, sets up some options
   * and begins the whole process.
   *
   * NOTES:
   * - This needs to be an effect
   * - It has required listener clean ups
   * - Can not re-render map created from this hook, will error.
   *
   */

  const prepareMap = useCallback(
    (mapId: string): void => {
      const options = {
        center: startPosition,
        styles: [
          {
            featureType: 'poi',
            stylers: [{ visibility: 'off' }],
          },
        ],
        disableDefaultUI: true,
        // The zoom state was fixed on the first render, which can be long before the map is
        // created — a caller that learns how it wants the map framed in between is read here.
        zoom: initialZoom ?? zoom,
        gestureHandling: disableControls ? 'none' : 'greedy',
      } as google.maps.MapOptions;

      const mapInstance = new window.google.maps.Map(domRef?.current!, options);
      const setClampedMapBounds = () => {
        const mapCenter = mapInstance.getCenter() as google.maps.LatLng;

        ReactDOM.unstable_batchedUpdates(() => {
          setCenter({
            lat: mapCenter.lat(),
            lng: mapCenter.lng(),
          });
        });
      };

      window.google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
        const mapZoom = mapInstance.getZoom();
        setZoom(mapZoom!);
        setClampedMapBounds();
      });

      /**
       *  Called by dragstart and zoom_changed listeners to prevent keyboard remaining open on android devices
       *  when dragging/zooming map while search field has focus
       */
      const blurActiveElement = () => {
        const activeElement = document.activeElement as HTMLElement | null;
        activeElement?.blur?.();
      };

      addListener('dragstart', mapInstance, blurActiveElement, mapId);

      addListener('bounds_changed', mapInstance, debounce(setClampedMapBounds, 500), mapId);

      // The debounce above trails the gesture, so a consumer acting on the drag it just
      // handled — "search this area" does — would read the centre from before it.
      addListener('dragend', mapInstance, setClampedMapBounds, mapId);

      addListener(
        'zoom_changed',
        mapInstance,
        debounce(() => {
          const mapZoom = mapInstance.getZoom();
          setZoom(mapZoom!);
        }, 500),
        mapId
      );

      Object.keys(eventListeners).forEach(eventName => {
        const handlers = eventListeners[eventName];
        if (typeof handlers === 'function') {
          addListener(eventName, mapInstance, handlers, mapId);
        }
        if (Array.isArray(handlers)) {
          handlers.forEach(handler => {
            addListener(eventName, mapInstance, handler, mapId);
          });
        }
      });
      // Set a minZoom option to prevent the user to zoom out too much
      mapInstance.setOptions({ minZoom: MIN_MAP_ZOOM });
      setMap(mapInstance);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disableControls, eventListeners, initialZoom, zoom]
  );

  useEffect(() => {
    // The map instance and every marker must come from the same copy of the Maps
    // API, so we wait on the script this app already loads instead of pulling in a
    // second one.
    const shouldLoad = !!domRef.current && libraryLoaded && !map;

    if (!shouldLoad) {
      return;
    }

    prepareMap(getRandomId());

    // The listeners live as long as the map, not this effect: when the Maps API is already
    // loaded, this effect re-runs right after creating the map, and the `!map` guard above
    // would never add them back. They are cleared on unmount instead.
    /*
     * NOTE:
     * - Only run when we know the map container has been loaded in the dom ~ isMapMounted
     * - isMapMounted starts as false and is only set to true when we have a dom ref
     * - And the domRef.current will be set
     */
  }, [isMapMounted, libraryLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => () => {
      Object.keys(mapListeners.current).forEach(clearListenerCache);
    },
    []
  );

  const onLoadedIntoDom = useCallback((ref: HTMLDivElement) => {
    if (!domRef.current && ref) {
      domRef.current = ref;
      setIsMapMounted(true);
    }
  }, []);

  // `prepareMap` runs once, so a caller that takes the guest's gestures away only after the
  // map exists — store details freezes the map behind it — has to be applied to the instance.
  useEffect(() => {
    map?.setOptions({ gestureHandling: disableControls ? 'none' : 'greedy' });
  }, [map, disableControls]);

  const resetZoom = useCallback(async () => {
    if (!map) {
      return;
    }

    map.setZoom(DEFAULT_MAP_ZOOM);
  }, [map]);

  const zoomBy = useCallback(
    (delta: number) => {
      if (!map) {
        return;
      }

      const currentZoom = map.getZoom() ?? DEFAULT_MAP_ZOOM;
      const nextZoom = Math.min(Math.max(currentZoom + delta, MIN_MAP_ZOOM), MAX_MAP_ZOOM);

      if (nextZoom !== currentZoom) {
        map.setZoom(nextZoom);
        setZoom(nextZoom);
      }
    },
    [map]
  );

  const panTo = useCallback<PanToFn>(
    async (newPosition, options) => {
      if (!map) {
        return;
      }

      if (isValidPosition(newPosition)) {
        map.panTo(newPosition);
      }

      // A requested zoom applies in both directions: the caller is framing the target, not
      // just making sure it is on screen.
      if (options?.zoom != null) {
        const nextZoom = Math.min(Math.max(options.zoom, MIN_MAP_ZOOM), MAX_MAP_ZOOM);

        if (nextZoom !== map.getZoom()) {
          map.setZoom(nextZoom);
          setZoom(nextZoom);
        }

        return;
      }

      if (map.getZoom()! < DEFAULT_MAP_ZOOM && newPosition !== defaultPosition) {
        map.setZoom(DEFAULT_MAP_ZOOM);
      }
    },
    [defaultPosition, map]
  );

  useEffect(() => {
    if (map && isValidPosition(position)) {
      panTo(position);
    }
    // We recreate a new object above each render, which makes a simple object
    // compare fail. We only want to run this effect when the actual values change
  }, [map, position?.lat, position?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * Creates a marker for a given type and location. If marker
   * already placed on map, updates accordingly.
   */
  const createMarker = useCallback(
    (markerInput: IMarkerInput): void => {
      if (!libraryLoaded || !map || !isMapMounted) {
        return;
      }

      const { id, type, location, onPress, title } = markerInput;

      if (!isValidPosition(location)) {
        return; // bail if location is not known
      }

      // This updates the marker if it has already been added to map.
      // Mirrors `getKeyForMarkerInput`, which can't be imported here: marker-utils pulls in
      // the native icon components, and the .web icon variants only export SVG strings.
      const isSingleUseMarker = [
        MarkerTypes.User,
        MarkerTypes.Driver,
        MarkerTypes.DeliveryAddress,
      ].includes(type);
      const key = isSingleUseMarker ? type : (id ?? `${location.lat}${location.lng}`);

      const existing = markers.current[key];
      if (existing) {
        // Replacing the icon in place can leave a stale click target, so rebuild
        // the overlay when the pin treatment changes (selected <-> unselected).
        if (existing.type !== type) {
          existing.marker.setMap(null);
          delete markers.current[key];
        } else {
          existing.marker.setMap(map);
          window.google.maps.event.clearInstanceListeners(existing.marker);
          if (onPress) {
            window.google.maps.event.addListener(existing.marker, 'click', onPress);
          }
          existing.marker.setPosition(location);
          existing.marker.setTitle(title ?? null);
          return;
        }
      }

      // A teardrop marks its location with its tip rather than with the bottom centre of its
      // image, so it is anchored on the tip the artwork declares instead of on Google Maps'
      // default.
      const iconUri = markersUris[type];
      const icon = isPinMarkerType(type)
        ? { url: iconUri, anchor: new window.google.maps.Point(PIN_ART_TIP.x, PIN_ART_TIP.y) }
        : iconUri;
      const marker = new window.google.maps.Marker({
        position: location,
        map,
        clickable: !!onPress,
        icon,
        // A clickable marker renders as a button, so it needs a name to be announced.
        title,
        // A delivery address is often where the guest is standing, so its pin has to sit above
        // their location dot rather than have its tip covered by it.
        zIndex:
          isSelectedStoreMarkerType(type) || type === MarkerTypes.DeliveryAddress
            ? 1000
            : undefined,
      });
      if (onPress) {
        window.google.maps.event.addListener(marker, 'click', onPress);
      }

      markers.current[key] = { marker, type };
      return;
    },
    [libraryLoaded, map, isMapMounted]
  );

  const removeMarkers = useCallback((ids: string[]) => {
    ids.forEach(id => {
      const existing = markers.current[id];

      if (!existing) {
        return;
      }

      window.google.maps.event.clearInstanceListeners(existing.marker);
      existing.marker.setMap(null);
      delete markers.current[id];
    });
  }, []);

  const fitAndCenterFromCoords = useCallback<FitAndCenterFromCoordsFn>(
    (coordsArray: CenterPoint[]) => {
      if (!libraryLoaded || !map || !isMapMounted) {
        return;
      }

      if (coordsArray.length >= 2) {
        const markerBounds = coordsArray.reduce((bounds, coord) => {
          bounds.extend(coord);
          return bounds;
        }, new window.google.maps.LatLngBounds());

        const marker1 = new window.google.maps.LatLng({
          lat: markerBounds.getSouthWest().lat(),
          lng: markerBounds.getSouthWest().lng(),
        });
        const marker2 = new window.google.maps.LatLng({
          lat: markerBounds.getNorthEast().lat(),
          lng: markerBounds.getNorthEast().lng(),
        });

        if (!marker1.equals(marker2)) {
          // Adding a 100px padding around the bounds to cover for
          // icons at the edge
          map.fitBounds(markerBounds, 100);
        }
      }
    },
    [libraryLoaded, map, isMapMounted]
  );

  const mapElement = React.createElement(MapContainer, { ref: onLoadedIntoDom });

  return {
    center,
    createMarker,
    removeMarkers,
    fitAndCenterFromCoords,
    map: mapElement,
    panTo,
    zoom,
    resetZoom,
    zoomBy,
    isMapReady: isMapMounted,
  };
};

export default useMap;
